import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

/**
 * AI-powered hero search bar. User types a natural-language query and Claude
 * returns structured filters (service, city, state, type, min_rating).
 * On result, we navigate to /search with the params applied.
 */
export default function AISearchBar() {
  const nav = useNavigate();
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [reply, setReply] = useState("");

  const submit = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setReply("");
    try {
      const { data } = await api.post("/ai/search-assist", { query });
      const params = new URLSearchParams();
      if (data.service) params.set("service", data.service);
      if (data.city) params.set("city", data.city);
      if (data.state) params.set("state", data.state);
      if (data.type && ["mobile", "clinic", "both"].includes(data.type)) params.set("type", data.type);
      if (data.min_rating) params.set("min_rating", data.min_rating);
      if (data.reply) setReply(data.reply);
      // tiny pause so the user sees the AI confirmation, then navigate
      setTimeout(() => nav(`/search?${params.toString()}`), 600);
    } catch {
      setReply("Couldn't understand that — try a simpler search.");
      setLoading(false);
    }
  };

  return (
    <div className="w-full" data-testid="ai-search-bar">
      <form
        onSubmit={submit}
        className="bg-white rounded-2xl p-2 shadow-xl shadow-stone-900/5 border border-stone-200 flex items-center gap-2"
      >
        <div className="px-3 py-2 flex items-center gap-2 text-[#064E3B]">
          <Sparkles className="w-5 h-5" />
          <span className="hidden sm:inline text-xs uppercase tracking-wider font-semibold whitespace-nowrap">AI Search</span>
        </div>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Try: mobile NAD+ in Miami under $400 with 4+ stars"
          className="flex-1 border-0 px-2 focus-visible:ring-0 shadow-none text-base"
          data-testid="ai-search-input"
        />
        <Button
          type="submit"
          disabled={loading || !query.trim()}
          className="bg-[#064E3B] hover:bg-[#053d2f] text-white rounded-xl px-5 font-semibold"
          data-testid="ai-search-submit"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Search <ArrowRight className="w-4 h-4 ml-1" /></>}
        </Button>
      </form>
      {reply && (
        <div className="mt-2 text-xs text-[#064E3B] flex items-center gap-1.5 px-3">
          <Sparkles className="w-3 h-3" /> {reply}
        </div>
      )}
    </div>
  );
}
